<script lang="ts" setup>
import {
  BellIcon,
  BellAlertIcon,
  XCircleIcon,
  ArrowPathIcon,
  CheckIcon,
  CheckCircleIcon,
} from '@heroicons/vue/24/outline'
import { ClockIcon } from '@heroicons/vue/24/solid'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { computed, ref } from 'vue'
import {
  NotificationData,
  NotificationsData,
  NotificationCategory,
} from '@/Support/Communications/Interfaces/NotificationData'
import { Notification } from '@/Support/Communications/Classes/Notification'
import { routing } from '@/Support/Routing/Functions/routing'
import { t } from '@/Support/lang'
import { storeToRefs } from 'pinia'
import { useNotifications } from '@/stores/notifications'
import { useAlertModal } from '@/stores/alertModal'
import Button from '@/Components/App/Forms/Button.vue'
import { ButtonAction } from '@/Support/Forms/Interfaces/Button'
import Pagination from '@/Layouts/App/Components/Pagination.vue'
import { PaginationParams } from '@/Support/Queries/Interfaces/PaginationParams'
import { PostableParams } from '@/Support/Communications/Interfaces/PostsParams'

const notificationsStore = useNotifications()
const { notificationsData, loadingNotifications } =
  storeToRefs(notificationsStore)
const notifications = computed(
  () =>
    notificationsData.value?.data.map(
      (data: NotificationData) => new Notification(data),
    ) || [],
)
const { loadNotifications } = notificationsStore
const unread = computed(
  () =>
    notificationsData.value?.data?.filter(notification => notification.unread)
      .length,
)

interface Params extends PaginationParams {
  sort: string
  filter?: {
    with_search?: string | null
  }
}

const originalParams = {
  page: 1,
  sort: '-created_at',
  limit: 20,
}
const params = ref<Params>(originalParams)
loadNotifications(params.value)

const allRead = async () => {
  newPromptWarningAlert(
    'Please confirm',
    t('«FEMALE»Are you sure you want to mark all as read'),
    t("Yes, I'm sure"),
    async () => {
      const response = await window.axios.post(
        routing('communications.notifications.all.read'),
      )
      notificationsData.value = response.data as NotificationsData
      await loadNotifications(params.value)
    },
  )
}

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { newPromptWarningAlert } = useAlertModal()

const see = (notification: Notification) => {
  if (!notification.url) {
    return
  }
  emit('close')
  openWindow.value = false

  if (notification.confirmation) {
    confirm(notification)
  } else {
    visitUrl(notification)
  }
}

const confirm = (notification: Notification) => {
  const confirmationLabel =
    notification.data.confirmation_label ||
    t('Are you sure you want to perform the following action: :action?', {
      action: notification.urlLabelButton,
    })
  newPromptWarningAlert(
    'Please confirm',
    confirmationLabel,
    t("Yes, I'm sure"),
    () => visitUrl(notification),
  )
}

const visitUrl = (notification: Notification) => {
  notification.visitUrl(undefined, {
    onFinish: () => {
      loadNotifications(params.value)
    },
  })
}

const openWindow = ref(false)

const toggle = () => {
  if (notifications.value.length) {
    openWindow.value = !openWindow.value
  }
}

const changePage = (page: number) => {
  params.value.page = page
  loadNotifications(params.value)
}
</script>
<template>
  <Menu as="div" class="relative inline-block text-left">
    <div>
      <MenuButton
        class="shrink-0 p-1 text-neutral-500 lg:text-neutral-200 rounded-full hover:text-neutral-400 lg:hover:text-white hover:bg-white/10 focus:outline-hidden"
        @click="toggle"
      >
        <span class="sr-only">
          {{ __('View notifications') }}
        </span>
        <BellAlertIcon
          v-if="unread"
          v-tooltip="__('You have unread notifications')"
          aria-hidden="true"
          class="h-6 w-6 text-tertiary-300"
        />
        <BellAlertIcon
          v-if="unread"
          v-tooltip="__('You have unread notifications')"
          aria-hidden="true"
          class="h-6 w-6 text-tertiary-300 animate-ping absolute top-0 mt-1 opacity-50"
        />
        <BellIcon v-else aria-hidden="true" class="h-6 w-6" />
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-show="openWindow && notifications.length"
        class="fixed sm:absolute right-0 top-0 sm:top-8 z-10 mt-2 w-full sm:w-96 sm:origin-top-right rounded-md sm:backdrop-blur-xs bg-white sm:bg-white/75 dark:bg-neutral-700 sm:dark:bg-neutral-700/75 shadow-lg ring-1 ring-black/5 focus:outline-hidden"
      >
        <MenuItems static class="h-full sm:max-h-128 overflow-y-auto">
          <div class="py-1">
            <MenuItem
              v-for="notification in notifications"
              :key="notification.id"
              v-slot="{ active }"
            >
              <a
                :class="[
                  active
                    ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-700'
                    : 'text-neutral-700',
                  'block px-4 py-2 text-sm cursor-pointer',
                ]"
                v-tooltip.left="
                  notification.url
                    ? __(notification.urlLabelButton || 'Visit')
                    : ''
                "
                @click="see(notification)"
              >
                <div
                  class="relative flex items-center space-x-3 px-6 py-5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                >
                  <div class="shrink-0">
                    <ClockIcon
                      v-if="
                        notification.category === NotificationCategory.Reminder
                      "
                      class="w-10 h-10 text-secondary-500 dark:text-secondary-600"
                    />
                    <CheckCircleIcon
                      v-else-if="
                        notification.category === NotificationCategory.Success
                      "
                      class="w-10 h-10 text-secondary-500 dark:text-secondary-600"
                    />
                    <img
                      v-else
                      class="h-10 w-10 rounded-full"
                      :src="notification.owner?.profilePhotoUrl"
                      :alt="notification.owner?.name"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex justify-between space-x-3">
                      <div class="min-w-0 flex-1">
                        <div class="block focus:outline-hidden">
                          <span class="absolute inset-0" aria-hidden="true" />
                          <p
                            v-if="!notification.system"
                            class="truncate text-sm font-medium text-neutral-500 dark:text-neutral-400 text-sm"
                          >
                            {{ notification.owner?.name }}
                          </p>
                          <p
                            class="mt-1 truncate text-neutral-900 dark:text-neutral-100 font-medium"
                            :class="{
                              'text-sm': notification.system,
                              'text-xs': !notification.system,
                            }"
                          >
                            {{ notification.title }}
                          </p>
                        </div>
                      </div>
                      <time
                        :datetime="notification.date"
                        class="shrink-0 whitespace-nowrap text-xs text-neutral-500 dark:text-neutral-300"
                      >
                        {{ notification.date }}
                      </time>
                      <span
                        v-show="notification.unread"
                        class="bg-tertiary-500 rounded-full w-2 h-2 opacity-75"
                      >
                      </span>
                    </div>
                    <div class="mt-1">
                      <p
                        class="text-xs text-neutral-600 dark:text-neutral-200 line-clamp-2"
                      >
                        {{ notification.content }}
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            </MenuItem>
          </div>
          <Pagination
            v-if="notificationsData"
            :data="notificationsData?.meta"
            class="mb-4"
            @change="changePage"
          />
        </MenuItems>

        <div
          class="bg-neutral-100 dark:bg-neutral-700 px-2 flex flex-col justify-around py-3 space-y-2 rounded-b-md"
        >
          <Button
            @click="allRead"
            class="grow items-center flex"
            :action="ButtonAction.Neutral"
          >
            <CheckIcon
              class="w-5 h-5 text-neutral-900 dark:text-neutral-100 mr-2"
            />
            {{ t('notification.window.mark-all-read') }}
          </Button>
          <div class="flex space-x-2">
            <Button
              @click="openWindow = false"
              class="grow"
              :action="ButtonAction.Neutral"
            >
              <XCircleIcon
                class="w-5 h-5 text-neutral-900 dark:text-neutral-100 mr-2"
              />
              {{ t('notification.window.close') }}
            </Button>
            <Button
              class="grow"
              :action="ButtonAction.Neutral"
              @click="loadNotifications(originalParams)"
            >
              <ArrowPathIcon
                :class="{
                  'animate-spin': loadingNotifications,
                }"
                class="w-5 h-5 text-neutral-900 dark:text-neutral-100 mr-2"
              />
              {{ t('notification.window.reload') }}
            </Button>
          </div>
        </div>
      </div>
    </transition>
  </Menu>
</template>
